import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  BookOpen,
  NotebookPen,
  GraduationCap,
  ScrollText,
  Headphones,
  Video,
  Package,
  FileText,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ProductCard } from "@/components/store/ProductCard";
import { getProductPageData } from "@/lib/store.functions";
import { addToCart, formatPrice } from "@/lib/store";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/store/products/$slug")({
  component: ProductDetail,
});

const TYPE_ICON: Record<string, LucideIcon> = {
  teaching: BookOpen,
  workbook: NotebookPen,
  course: GraduationCap,
  "study-guide": ScrollText,
  audio: Headphones,
  video: Video,
  bundle: Package,
};

function typeLabel(type: string) {
  return type
    .split("-")
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(" ");
}

function categoryLabel(c: string | null | undefined) {
  if (!c) return "Library";
  return c
    .split("-")
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(" ");
}

function formatBytes(b?: number | null) {
  if (!b) return null;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function renderMarkdown(md: string) {
  // Lightweight rendering: paragraphs from blank lines, with bold/italic.
  return md
    .trim()
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function ProductDetail() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const fetchData = useServerFn(getProductPageData);
  const { data, isLoading } = useQuery({
    queryKey: ["store", "product-page", slug],
    queryFn: () => fetchData({ data: { slug } }),
  });

  const [added, setAdded] = useState(false);

  const paragraphs = useMemo(
    () => (data?.product?.description ? renderMarkdown(data.product.description) : []),
    [data],
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream-50">
        <Navbar />
        <div className="mx-auto max-w-5xl px-6 py-24">
          <p className="font-sans text-charcoal-500">Loading…</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!data?.product) {
    return (
      <div className="min-h-screen bg-cream-50">
        <Navbar />
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <Eyebrow align="center">Not in the library</Eyebrow>
          <h1 className="mt-4 font-serif text-3xl text-navy-800">
            This resource could not be found.
          </h1>
          <Link
            to="/store"
            className="mt-6 inline-block font-sans text-sm font-semibold uppercase tracking-[0.18em] text-bronze-600 hover:text-bronze-400"
          >
            ← Back to the library
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const { product, files, related } = data;
  const Icon = TYPE_ICON[product.product_type] ?? BookOpen;
  const meta = [
    typeLabel(product.product_type),
    product.pages != null ? `${product.pages} pages` : null,
    product.duration_minutes != null ? `${product.duration_minutes} min` : null,
    files.length > 0
      ? files
          .map((f: { mime_type?: string | null; file_name?: string | null }) => {
            const ext = (f.file_name ?? "").split(".").pop()?.toUpperCase();
            return ext;
          })
          .filter((v, i, a) => v && a.indexOf(v) === i)
          .join(" · ")
      : null,
  ]
    .filter(Boolean)
    .join("  ·  ");

  const onAdd = () => {
    addToCart({
      product_id: product.id,
      slug: product.slug,
      title: product.title,
      price_cents: product.price_cents,
      thumbnail_url: product.thumbnail_url,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  const onBuyNow = () => {
    addToCart({
      product_id: product.id,
      slug: product.slug,
      title: product.title,
      price_cents: product.price_cents,
      thumbnail_url: product.thumbnail_url,
    });
    navigate({ to: "/store/cart" });
  };

  return (
    <div className="min-h-screen bg-cream-50 pb-28 md:pb-0">
      <Navbar />

      {/* Breadcrumb */}
      <nav
        aria-label="Breadcrumb"
        className="mx-auto max-w-6xl px-6 pt-10 font-sans text-[12px] uppercase tracking-[0.18em] text-charcoal-500"
      >
        <ol className="flex flex-wrap items-center gap-2">
          <li>
            <Link to="/store" className="hover:text-bronze-600">
              Store
            </Link>
          </li>
          <ChevronRight className="h-3 w-3 text-bronze-600/60" />
          <li>{categoryLabel(product.category)}</li>
          <ChevronRight className="h-3 w-3 text-bronze-600/60" />
          <li className="text-navy-800">{product.title}</li>
        </ol>
      </nav>

      <section className="mx-auto mt-8 max-w-6xl px-6">
        <div className="grid gap-12 md:grid-cols-[3fr_2fr]">
          {/* LEFT */}
          <div>
            <div className="overflow-hidden rounded-sm shadow-[0_24px_60px_-24px_rgba(168,139,92,0.35)]">
              {product.thumbnail_url ? (
                <img
                  src={product.thumbnail_url}
                  alt={product.title}
                  className="aspect-[4/5] w-full object-cover"
                />
              ) : (
                <div className="flex aspect-[4/5] w-full items-center justify-center bg-navy-900 text-cream-50/30">
                  <Icon className="h-24 w-24" strokeWidth={1.1} aria-hidden />
                </div>
              )}
            </div>

            {product.preview_url && (
              <a
                href={product.preview_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 font-sans text-sm font-semibold uppercase tracking-[0.18em] text-bronze-600 transition hover:text-bronze-400"
              >
                Preview sample →
              </a>
            )}

            <Tabs defaultValue="overview" className="mt-10">
              <TabsList className="h-auto w-full justify-start gap-6 rounded-none border-b border-bronze-600/30 bg-transparent p-0">
                <TabsTrigger
                  value="overview"
                  className="rounded-none border-b-2 border-transparent bg-transparent px-0 pb-3 font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-charcoal-500 data-[state=active]:border-bronze-600 data-[state=active]:bg-transparent data-[state=active]:text-navy-800 data-[state=active]:shadow-none"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="inside"
                  className="rounded-none border-b-2 border-transparent bg-transparent px-0 pb-3 font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-charcoal-500 data-[state=active]:border-bronze-600 data-[state=active]:bg-transparent data-[state=active]:text-navy-800 data-[state=active]:shadow-none"
                >
                  What's Inside
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-8">
                <article className="max-w-[560px] font-sans text-[16px] leading-[1.75] text-charcoal-900">
                  {paragraphs.length === 0 ? (
                    <p className="italic text-charcoal-500">
                      A full description for this resource is being prepared.
                    </p>
                  ) : (
                    paragraphs.map((p, i) => (
                      <p
                        key={i}
                        className={cn(
                          "mb-5",
                          i === 0 &&
                            "first-letter:float-left first-letter:mr-2 first-letter:font-serif first-letter:text-[56px] first-letter:font-bold first-letter:leading-[0.9] first-letter:text-bronze-600",
                        )}
                      >
                        {p}
                      </p>
                    ))
                  )}
                </article>
              </TabsContent>

              <TabsContent value="inside" className="mt-8">
                <ul className="max-w-[560px] space-y-3 font-sans text-[15px] text-charcoal-900">
                  {product.pages != null && (
                    <li className="flex gap-3">
                      <span className="mt-2 h-px w-4 bg-bronze-600" />
                      <span>{product.pages} pages of teaching</span>
                    </li>
                  )}
                  {product.duration_minutes != null && (
                    <li className="flex gap-3">
                      <span className="mt-2 h-px w-4 bg-bronze-600" />
                      <span>{product.duration_minutes} minutes of content</span>
                    </li>
                  )}
                  {files.length > 0 ? (
                    files.map((f) => (
                      <li key={f.id} className="flex gap-3">
                        <span className="mt-2 h-px w-4 bg-bronze-600" />
                        <span>
                          {f.file_name}
                          {formatBytes(f.file_size_bytes) && (
                            <span className="ml-2 text-charcoal-500">
                              ({formatBytes(f.file_size_bytes)})
                            </span>
                          )}
                        </span>
                      </li>
                    ))
                  ) : (
                    <li className="italic text-charcoal-500">
                      File details will be provided upon delivery.
                    </li>
                  )}
                </ul>
              </TabsContent>
            </Tabs>
          </div>

          {/* RIGHT */}
          <aside className="md:sticky md:top-24 md:self-start">
            <Eyebrow>{typeLabel(product.product_type)}</Eyebrow>
            <h1 className="mt-4 font-serif text-[32px] leading-tight text-navy-800">
              {product.title}
            </h1>
            {product.subtitle && (
              <p className="mt-3 font-sans text-[15px] text-charcoal-500">
                {product.subtitle}
              </p>
            )}

            <p className="mt-6 font-serif text-[40px] leading-none text-bronze-600">
              {formatPrice(product.price_cents)}
            </p>

            <div className="mt-6 hidden flex-col gap-3 md:flex">
              <PrimaryButton size="lg" onClick={onBuyNow}>
                Add to Library
              </PrimaryButton>
              <Button
                variant="outline"
                onClick={onAdd}
                className="h-12 rounded-[2px] border-bronze-600 bg-transparent font-sans text-[13px] font-semibold uppercase tracking-[0.05em] text-navy-800 hover:bg-bronze-400 hover:text-navy-900"
              >
                {added ? "Added to cart ✓" : "Add to Cart"}
              </Button>
            </div>

            {meta && (
              <p className="mt-6 font-sans text-[12px] uppercase tracking-[0.18em] text-charcoal-500">
                {meta}
              </p>
            )}

            <div className="mt-8">
              <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
                What you'll receive
              </p>
              <ul className="mt-3 space-y-2 font-sans text-[13px] text-charcoal-900">
                {files.length > 0 ? (
                  files.map((f) => (
                    <li key={f.id} className="flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-bronze-600" />
                      {f.file_name}
                    </li>
                  ))
                ) : (
                  <li className="italic text-charcoal-500">
                    Digital materials delivered after agreement is signed.
                  </li>
                )}
              </ul>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <div className="mt-24">
            <Eyebrow>From the same teaching stream</Eyebrow>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <ProductCard key={r.id} product={r} />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Mobile sticky bottom bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-bronze-600/30 bg-cream-50 px-4 py-3 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.18)] md:hidden">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-serif text-[22px] leading-none text-bronze-600">
              {formatPrice(product.price_cents)}
            </p>
            <p className="mt-1 font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
              {typeLabel(product.product_type)}
            </p>
          </div>
          <PrimaryButton onClick={onBuyNow}>Add to Library</PrimaryButton>
        </div>
      </div>

      <Footer />
    </div>
  );
}